/**
 * ConvertLeadModal.tsx — the Inbound Leads → Pipeline handoff ceremony
 * (§8.F; CloseDealModal is the form-modal precedent).
 *
 * The load-bearing rule: the server refuses a convert with a NAMED 400 when
 * neither the lead nor the body carries a company. That 400 is the backstop,
 * never the user-facing path — a lead without a company gets a required
 * Company field here, and confirm stays disabled until it (and title) are
 * filled (leadsFlow.convertConfirmable, vitest-pinned). A lead WITH a
 * company never sends one: the deal inherits the contact's server-side, and
 * a body company would silently override it.
 *
 * On success the caller receives the WIRE deal, born at new_lead on the DB
 * default — it navigates to the born deal via selectedDealId (the markBidWon
 * behavioral precedent). busy disables confirm after the first click: the
 * server re-convert is idempotent, but the UI still owes no double-submit
 * path.
 *
 * C3a (§15.10.11): the server may REFUSE a convert with a 409 whose body
 * carries the matcher's candidates (candidatesFrom). That is not the advisory
 * pre-flight — it is the write path saying no, at submit time, on the
 * server's own matching. The chooser tile then switches to its refusal form:
 * the server's sentence verbatim, the candidates it found, "Use this account"
 * as before, and "Create anyway", which resubmits with createAnyway: true (the
 * override the server records). Against a server without the refusal nothing
 * here ever renders — the 409 simply never arrives.
 *
 * P1: colors/sizes flow from src/theme/tokens.ts (canonical P1 design system).
 */
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import type { SalesDeal as WireDeal, SalesMatchCandidate } from '../../../../api/types'
import type { SalesLead } from '../../types'
import { convertLead, fetchSalesMatches, useAsync, candidatesFrom } from '../../api'
import { refreshSalesFigures } from '../../salesFigures'
import { apiErrorMessage } from '../../../../api/client'
import { toast } from 'sonner'
import { overlay } from '../../../../theme/tokens'
import {
  buildConvertBody, convertConfirmable, needsCompanyField, matchesQueryFor,
} from './leadsFlow'
import { OwnerSelect } from './OwnerSelect'
import { AdvisoryChooser, RefusalChooser, type CandidateAction } from './DuplicateChooser'

// ── C2 / C3a: the duplicate chooser ─────────────────────────────────────────
// Rendered by DuplicateChooser.tsx — the ONE renderer every create surface
// shares (§15.10.11). Here an ACCOUNT candidate is pickable (C2.3: the pick
// sets body.company to its canonical name) and a CONTACT candidate is
// information only (convert is per-lead; attaching it would be a merge, P1-4).

export function ConvertLeadModal({ lead, onCancel, onConverted }: {
  lead: SalesLead
  onCancel: () => void
  onConverted: (wire: WireDeal) => void
}) {
  const [title, setTitle] = useState('')
  const [company, setCompany] = useState('')
  const [value, setValue] = useState('')
  const [expectedClose, setExpectedClose] = useState('')
  // Holds the selected employee's ID (E360-2 owner wire), not a name.
  const [assignedEmployeeId, setAssignedEmployeeId] = useState('')
  const [busy, setBusy] = useState(false)
  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onCancel])

  const companyRequired = needsCompanyField(lead.company)

  // C2: the pre-flight. Queried ON OPEN with the lead's own email + company
  // (fixed inputs — one call answers the question before the rep types), and
  // re-queried on BLUR of the Company field only when that field renders
  // (lead has none), because that is the only matching input that can change.
  // matchCompany is the last BLURRED value, so keystrokes never fire requests.
  const [matchCompany, setMatchCompany] = useState('')
  const [pickedCompany, setPickedCompany] = useState('')
  const matchesQ = useAsync(
    () => fetchSalesMatches(matchesQueryFor(lead, matchCompany)),
    [lead.id, lead.email, lead.company, matchCompany],
  )
  const candidates = matchesQ.data ?? []
  // A pick is a toggle: picking the selected account again clears it. There
  // is no dismiss button — the chooser is advisory and nothing waits on it.
  const pick = (c: SalesMatchCandidate) => {
    const name = c.accountName || c.displayName
    setPickedCompany(prev => (prev === name ? '' : name))
  }
  // What a row offers (DuplicateChooser): accounts the pick, contacts nothing.
  const actionFor = (c: SalesMatchCandidate): CandidateAction | undefined => (
    c.type === 'account'
      ? {
        label: 'Use this account', pressedLabel: 'Selected',
        pressed: pickedCompany !== '' && pickedCompany === (c.accountName || c.displayName),
        onAct: () => pick(c),
      }
      : undefined
  )
  const linkedFooter = pickedCompany !== '' ? (
    <div className="text-11 text-ink-faint mt-2">
      The deal will be linked to <span className="text-ink-body font-medium">{pickedCompany}</span>.
    </div>
  ) : null

  // C2.4, load-bearing: convertConfirmable reads NOTHING from matchesQ. A
  // failed or slow /matches call cannot disable Confirm — the guard is
  // advisory, and an outage must never stop a rep converting a lead.
  const formState = { leadCompany: lead.company, title, company, pickedCompany, value, expectedClose, assignedEmployeeId, busy }
  const confirmable = convertConfirmable(formState)

  // C3a: the server's refusal — the 409's candidates and its sentence. Set
  // only from a failed submit, cleared on the next submit, and cleared when
  // the TYPED company changes (the refusal was about the previous text — see
  // the Company input's onChange). A pick does NOT clear it: "Use this
  // account" inside the refusal list marks the row Selected and the rep
  // resubmits from there.
  const [refusal, setRefusal] = useState<{ message: string; candidates: SalesMatchCandidate[] } | null>(null)

  async function confirm(createAnyway: boolean) {
    if (!confirmable) return
    setBusy(true)
    setRefusal(null)
    try {
      const wire = await convertLead(lead.id, buildConvertBody({ ...formState, createAnyway }))
      toast.success('Lead converted — deal created in the pipeline at New Lead')
      // Row 84: a conversion CREATES an open deal — the sidebar's Pipeline
      // count moves, so the shared figures are re-read.
      void refreshSalesFigures()
      onConverted(wire)
    } catch (e) {
      const refused = candidatesFrom(e)
      if (refused) {
        // The sentence is the server's (C-10: always a real sentence); the
        // fallback only fires if a future server sends candidates with a
        // bare codeword, which apiErrorMessage degrades by design.
        setRefusal({ message: apiErrorMessage(e, 'The server refused this convert as a possible duplicate.'), candidates: refused })
      } else {
        toast.error(apiErrorMessage(e, 'Failed to convert lead'))
      }
      setBusy(false)
    }
  }

  const inputCls = 'w-full px-4 py-3 bg-surface-control border border-edge-control rounded-control text-13 text-ink-body placeholder:text-ink-faint focus:outline-none focus:border-accent'

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: overlay.scrim, backdropFilter: 'blur(2px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onCancel() }}>
      {/* S-Tier (slice 3): a centered dialog is a floating widget — .k-card
          (18px, --panel, --shadow-card); the summary box is a nested tile. */}
      <div role="dialog" aria-modal="true" aria-label="Convert lead to deal"
        className="k-card"
        style={{ width: 'min(440px, calc(100vw - 32px))' }}>

        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-edge-row">
          <div className="text-detail-title font-bold text-ink-primary">Convert lead to deal</div>
          <button onClick={onCancel} aria-label="Cancel"
            className="w-8 h-8 flex items-center justify-center rounded-control text-ink-faint hover:bg-surface-nav-hover transition-colors">
            <X size={16} />
          </button>
        </div>

        <div className="px-6 py-5 flex flex-col gap-4">
          {/* Read-only lead summary */}
          <div className="rounded-tile border border-edge-row bg-surface-panel px-4 py-3">
            <div className="text-13 font-semibold text-ink-primary">{lead.name}</div>
            <div className="text-12 text-ink-secondary mt-1">
              {lead.company ? lead.company : 'No company on this lead'}
              {lead.source ? ` · via ${lead.source}` : ''}
            </div>
          </div>

          {/* C3a: the REFUSAL form of the chooser — the write path said no.
              Same rows and actions as the advisory tile, plus "Create anyway";
              the advisory tile is hidden meanwhile (one list — the server's). */}
          {refusal && (
            <RefusalChooser eyebrow="Convert refused — possible duplicate" message={refusal.message}
              hint="Found by the server when you submitted — not the advisory check. Use one of these accounts, or create anyway."
              candidates={refusal.candidates} actionFor={actionFor} footer={linkedFooter}
              createAnywayNote="Creating anyway is recorded against the new account."
              onCreateAnyway={() => confirm(true)} createAnywayDisabled={!confirmable} />
          )}

          {/* C2: the duplicate chooser — advisory, never gating. Every state is
              honest: checking, unavailable (the read failed — NOT "none"),
              none found, or the candidates with WHY each matched. */}
          {!refusal && (
            <AdvisoryChooser loading={matchesQ.loading} error={matchesQ.error} candidates={candidates} actionFor={actionFor}
              copy={{
                checking: 'Checking for existing contacts and accounts…',
                unavailable: 'Duplicate check unavailable — you can still convert.',
                empty: 'No existing contacts or accounts look like this lead.',
              }}
              footer={linkedFooter} />
          )}

          <div>
            <label className="block text-12 font-semibold text-ink-body mb-2">Deal title *</label>
            <input value={title} onChange={e => setTitle(e.target.value)} autoFocus
              placeholder={`e.g. ${lead.company || lead.name} — first engagement`} className={inputCls} />
          </div>

          {companyRequired && (
            <div>
              <label className="block text-12 font-semibold text-ink-body mb-2">Company *</label>
              {/* C2.2: the typed company re-queries the chooser on BLUR — the
                  one matching input that can change on this form. */}
              <input value={company} onChange={e => { setCompany(e.target.value); setRefusal(null) }}
                onBlur={() => setMatchCompany(company)}
                placeholder="A deal needs a company" className={inputCls} />
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-12 font-semibold text-ink-body mb-2">Value (£)</label>
              <input type="number" min="0" step="0.01" value={value} onChange={e => setValue(e.target.value)}
                placeholder="0.00" className={inputCls} />
            </div>
            <div>
              <label className="block text-12 font-semibold text-ink-body mb-2">Expected close</label>
              <input type="date" value={expectedClose} onChange={e => setExpectedClose(e.target.value)}
                className={inputCls} />
            </div>
          </div>

          <div>
            <label className="block text-12 font-semibold text-ink-body mb-2">Assignee</label>
            {/* SB-2: picklist from employees; empty inherits the lead's
                assignee (the module's §8.D fallback), so the empty option
                says so instead of "Unassigned" when there is one. */}
            <OwnerSelect value={assignedEmployeeId} onChange={setAssignedEmployeeId}
              className={`${inputCls} bg-surface-card`}
              inheritHint={lead.assignedTo ? `Inherits ${lead.assignedTo}` : undefined} />
          </div>

          <div className="text-12 text-ink-secondary">
            Creates a pipeline deal at the New Lead stage; this lead leaves the Inbound Leads queue.
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-6 pb-5">
          <button onClick={onCancel} className="k-btn-secondary">
            Cancel
          </button>
          <button onClick={() => confirm(false)} disabled={!confirmable}
            className="k-btn k-btn--lg disabled:opacity-60">
            {busy ? 'Converting…' : 'Convert to deal'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}
